// src/pages/reporting/DayForecastForm.jsx
// Per-day DDS + HYG forecast form rendered in the right panel when a calendar day is clicked.
// Sends forecast_dds and forecast_hyg to the API; the backend auto-computes the total forecast.
import { useMemo, useState, useEffect } from "react";
import { FormSubmit, FormField, MutationError, ForecastFormActions } from "@common";
import { useCreateForecast, useUpdateForecast, usePractitioners } from "@api/hooks";
import { getApiErrorMessage } from "@utils/apiErrorUtils";
import { toast } from "react-hot-toast";
import { formatMoney, parseNumber } from "@utils/format";
import { useFormInteractionTime } from "@hooks/useFormInteractionTime";
import { FORM_IDS, torontoToday } from "@utils";
import { getRoster } from "@utils/practitionerUtils";
import { normalizeTypeForUi } from "@utils/utils";

// Format a YYYY-MM-DD string as a human-readable label for the form heading
function formatDateLabel(dateStr) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

let _entryId = 0;
function newEntry() {
  return { id: ++_entryId, name: "", amount: "" };
}

const INPUT_CLASS =
  "w-full rounded-xl border px-4 py-3 text-palette-gray-primary outline-none transition-colors border-palette-gray-border bg-white shadow-sm hover:border-palette-gray-placeholder focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-label/30";
const INPUT_DISABLED_CLASS =
  "w-full rounded-xl border px-4 py-3 text-palette-gray-primary outline-none transition-colors cursor-not-allowed border-palette-gray-border bg-palette-gray-subtle text-palette-gray-text";
const ENTRY_SELECT_CLASS =
  "flex-1 min-w-0 rounded-lg border border-palette-gray-border bg-white px-2 py-2 text-sm text-palette-gray-primary shadow-sm focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-border focus:outline-none";
const ENTRY_AMOUNT_CLASS =
  "w-28 rounded-lg border border-palette-gray-border bg-white px-2 py-2 text-sm text-palette-gray-primary shadow-sm focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-border focus:outline-none";

export default function DayForecastForm({
  clinic,
  date,
  existingForecast,
  mtd = 0, // Actual production from month start up to (not including) this date
  canEdit = true, // False for view-only users or dates in the past
  forecastsByDate = {}, // Full month's forecast map — used to sum remaining-day forecasts
}) {
  const {
    formTrackingProps,
    recordSubmitSuccess,
    reset: resetInteractionTimer,
  } = useFormInteractionTime(FORM_IDS.FORECAST_ENTRY);

  const createForecastMutation = useCreateForecast();
  const updateForecastMutation = useUpdateForecast();

  // Whether a saved forecast record already exists for this clinic + date
  const hasExisting = !!existingForecast?.id;
  const [isEditing, setIsEditing] = useState(false);
  const [justCreated, setJustCreated] = useState(false);

  // Controlled inputs for the DDS and HYG forecast values (used when no sub-entries are present)
  const [ddsInput, setDdsInput] = useState("");
  const [hygInput, setHygInput] = useState("");

  // Itemized sub-entries: each has a provider name and amount
  const [ddsEntries, setDdsEntries] = useState([]);
  const [hygEntries, setHygEntries] = useState([]);

  // Fetch practitioners for this clinic to populate provider dropdowns
  const { data: practitionersData } = usePractitioners(
    { clinic, limit: 200 },
    { enabled: !!clinic },
  );
  const roster = getRoster(practitionersData).filter((p) => p.status !== "inactive");
  const ddsProviders = roster.filter((p) => normalizeTypeForUi(p.practitioner_type) === "DDS");
  const hygProviders = roster.filter((p) => normalizeTypeForUi(p.practitioner_type) === "HYG");

  // Populate inputs from the existing forecast whenever the selected date or clinic changes
  useEffect(() => {
    if (existingForecast) {
      setDdsInput(
        existingForecast.forecast_dds != null ? String(existingForecast.forecast_dds) : "",
      );
      setHygInput(
        existingForecast.forecast_hyg != null ? String(existingForecast.forecast_hyg) : "",
      );
    } else {
      setDdsInput("");
      setHygInput("");
    }
    setIsEditing(false);
    setJustCreated(false);
    setDdsEntries([]);
    setHygEntries([]);
  }, [existingForecast, date]);

  // Detect a negative sign BEFORE parseNumber strips it.
  const ddsError = ddsInput.trim().startsWith("-") ? "Value cannot be negative" : null;
  const hygError = hygInput.trim().startsWith("-") ? "Value cannot be negative" : null;

  // Sums from the sub-entry rows
  const ddsEntriesSum = ddsEntries.reduce((sum, e) => sum + (parseNumber(e.amount) || 0), 0);
  const hygEntriesSum = hygEntries.reduce((sum, e) => sum + (parseNumber(e.amount) || 0), 0);

  // When sub-entries exist, use their sum; otherwise fall back to the direct text input
  const dds = ddsEntries.length > 0 ? ddsEntriesSum : parseNumber(ddsInput);
  const hyg = hygEntries.length > 0 ? hygEntriesSum : parseNumber(hygInput);
  const forecastTotal = dds + hyg;

  // When sub-entries are being edited, freeze Total/Forecast-to-Month-End/Month-End to the last
  // saved values so those fields don't update live as the user types provider amounts.
  const useSubEntries = ddsEntries.length > 0 || hygEntries.length > 0;
  const displayedForecast = useSubEntries
    ? existingForecast?.forecast != null
      ? parseFloat(existingForecast.forecast)
      : 0
    : !isEditing && existingForecast?.forecast != null
      ? parseFloat(existingForecast.forecast)
      : forecastTotal;

  // Total = MTD actual production + today's forecast
  const total = (mtd || 0) + displayedForecast;

  // Sum of all saved DDS+HYG forecasts from TODAY through the last day of the clicked month.
  const forecastToMonthEnd = useMemo(() => {
    if (!date || !forecastsByDate) return 0;
    const [year, monthStr, dayStr] = date.split("-").map(Number);
    const lastDay = new Date(year, monthStr, 0).getDate();
    const todayStr = torontoToday();

    let sum = 0;
    for (let d = 1; d <= lastDay; d++) {
      const key = `${year}-${String(monthStr).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      if (key < todayStr) continue;

      if (d === dayStr) {
        sum += displayedForecast;
      } else {
        const f = forecastsByDate[key];
        if (!f) continue;
        if (f.forecast_dds != null && f.forecast_hyg != null) {
          sum += parseFloat(f.forecast_dds || 0) + parseFloat(f.forecast_hyg || 0);
        } else if (f.forecast != null) {
          sum += parseFloat(f.forecast || 0);
        }
      }
    }
    return sum;
  }, [date, forecastsByDate, displayedForecast]);

  // Inputs are read-only when viewing an existing record (until Edit is clicked) or user has no rights
  const forecastDisabled = hasExisting && !isEditing;
  const canInteract = !forecastDisabled && canEdit;

  // At least one non-zero forecast value is required, and neither field may be negative
  const canSubmit = (dds > 0 || hyg > 0) && mtd >= 0 && !ddsError && !hygError;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      const forecastData = { clinic, date, forecast_dds: dds, forecast_hyg: hyg };

      if (hasExisting) {
        await updateForecastMutation.mutateAsync({ id: existingForecast.id, data: forecastData });
        recordSubmitSuccess();
        toast.success("Forecast updated successfully!");
        setIsEditing(false);
      } else {
        const newForecast = await createForecastMutation.mutateAsync(forecastData);
        if (newForecast) {
          recordSubmitSuccess();
          toast.success("Forecast created successfully!");
          setJustCreated(true);
        }
      }
    } catch (error) {
      console.error("Failed to save forecast:", error);
      toast.error(getApiErrorMessage(error));
    }
  }

  // Cancel restores inputs to the last saved values and exits editing mode
  function handleCancel() {
    resetInteractionTimer();
    setDdsInput(
      existingForecast?.forecast_dds != null ? String(existingForecast.forecast_dds) : "",
    );
    setHygInput(
      existingForecast?.forecast_hyg != null ? String(existingForecast.forecast_hyg) : "",
    );
    setIsEditing(false);
    setDdsEntries([]);
    setHygEntries([]);
  }

  function addDdsEntry() {
    setDdsEntries((prev) => [...prev, newEntry()]);
  }
  function addHygEntry() {
    setHygEntries((prev) => [...prev, newEntry()]);
  }
  function updateDdsEntry(id, field, value) {
    setDdsEntries((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
  }
  function updateHygEntry(id, field, value) {
    setHygEntries((prev) => prev.map((e) => (e.id === id ? { ...e, [field]: value } : e)));
  }
  function removeDdsEntry(id) {
    setDdsEntries((prev) => prev.filter((e) => e.id !== id));
  }
  function removeHygEntry(id) {
    setHygEntries((prev) => prev.filter((e) => e.id !== id));
  }

  const isPending = createForecastMutation.isPending || updateForecastMutation.isPending;
  const isTransitioning = justCreated && !hasExisting;

  return (
    <FormSubmit
      onSubmit={handleSubmit}
      className="max-w-2xl mx-auto space-y-6"
      {...formTrackingProps}
    >
      {/* Date heading shown above the forecast fields */}
      <h3 className="text-sm font-semibold text-palette-gray-heading">{formatDateLabel(date)}</h3>

      {/* DDS Daily Forecast section */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="block text-sm font-medium">DDS Daily Forecast</span>
          {canInteract && (
            <button
              type="button"
              onClick={addDdsEntry}
              className="flex items-center justify-center w-5 h-5 rounded-full bg-palette-indigo-muted text-palette-indigo-badge hover:bg-palette-indigo-border transition-colors text-xs font-bold"
              aria-label="Add DDS entry"
            >
              +
            </button>
          )}
        </div>

        {ddsEntries.length > 0 ? (
          <>
            {/* Read-only total computed from sub-entries */}
            <div className={INPUT_DISABLED_CLASS}>{formatMoney(ddsEntriesSum)}</div>

            {/* Sub-entry rows: provider dropdown + amount input + remove */}
            {ddsEntries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-2 mt-2">
                <select
                  value={entry.name}
                  onChange={(e) => updateDdsEntry(entry.id, "name", e.target.value)}
                  className={ENTRY_SELECT_CLASS}
                >
                  <option value="" disabled>
                    Select DDS
                  </option>
                  {ddsProviders.map((p) => {
                    const name =
                      p.name || [p.prefix, p.first_name, p.last_name].filter(Boolean).join(" ");
                    return (
                      <option key={p.id} value={name}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={entry.amount}
                  onChange={(e) => updateDdsEntry(entry.id, "amount", e.target.value)}
                  className={ENTRY_AMOUNT_CLASS}
                />
                <button
                  type="button"
                  onClick={() => removeDdsEntry(entry.id)}
                  className="text-palette-gray-placeholder hover:text-palette-rose-hover transition-colors text-xl leading-none"
                  aria-label="Remove DDS entry"
                >
                  ×
                </button>
              </div>
            ))}
          </>
        ) : (
          <div className="mt-2">
            <input
              type="text"
              inputMode="decimal"
              placeholder="Enter DDS forecast value"
              value={ddsInput}
              onChange={(e) => setDdsInput(e.target.value)}
              disabled={forecastDisabled || !canEdit}
              className={forecastDisabled || !canEdit ? INPUT_DISABLED_CLASS : INPUT_CLASS}
            />
            {ddsError && <p className="mt-1 text-xs text-palette-rose-error">{ddsError}</p>}
          </div>
        )}
      </div>

      {/* HYG Daily Forecast section */}
      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="block text-sm font-medium">HYG Daily Forecast</span>
          {canInteract && (
            <button
              type="button"
              onClick={addHygEntry}
              className="flex items-center justify-center w-5 h-5 rounded-full bg-palette-emerald-muted text-palette-emerald-badge hover:bg-palette-emerald-border transition-colors text-xs font-bold"
              aria-label="Add HYG entry"
            >
              +
            </button>
          )}
        </div>

        {hygEntries.length > 0 ? (
          <>
            {/* Read-only total computed from sub-entries */}
            <div className={INPUT_DISABLED_CLASS}>{formatMoney(hygEntriesSum)}</div>

            {/* Sub-entry rows: provider dropdown + amount input + remove */}
            {hygEntries.map((entry) => (
              <div key={entry.id} className="flex items-center gap-2 mt-2">
                <select
                  value={entry.name}
                  onChange={(e) => updateHygEntry(entry.id, "name", e.target.value)}
                  className={ENTRY_SELECT_CLASS}
                >
                  <option value="" disabled>
                    Select HYG
                  </option>
                  {hygProviders.map((p) => {
                    const name =
                      p.name || [p.prefix, p.first_name, p.last_name].filter(Boolean).join(" ");
                    return (
                      <option key={p.id} value={name}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={entry.amount}
                  onChange={(e) => updateHygEntry(entry.id, "amount", e.target.value)}
                  className={ENTRY_AMOUNT_CLASS}
                />
                <button
                  type="button"
                  onClick={() => removeHygEntry(entry.id)}
                  className="text-palette-gray-placeholder hover:text-palette-rose-hover transition-colors text-xl leading-none"
                  aria-label="Remove HYG entry"
                >
                  ×
                </button>
              </div>
            ))}
          </>
        ) : (
          <div className="mt-2">
            <input
              type="text"
              inputMode="decimal"
              placeholder="Enter HYG forecast value"
              value={hygInput}
              onChange={(e) => setHygInput(e.target.value)}
              disabled={forecastDisabled || !canEdit}
              className={forecastDisabled || !canEdit ? INPUT_DISABLED_CLASS : INPUT_CLASS}
            />
            {hygError && <p className="mt-1 text-xs text-palette-rose-error">{hygError}</p>}
          </div>
        )}
      </div>

      {/* MTD Production: cumulative actual production from the 1st up to (not including) this day */}
      <FormField label="MTD Production" value={formatMoney(mtd)} disabled />

      {/* Total: MTD actual + this day's forecast — quick combined reference */}
      <FormField label="Total" value={formatMoney(total)} disabled />

      {/* Sum of all saved daily forecasts from this day through the end of the month */}
      <FormField label="Forecast to Month End" value={formatMoney(forecastToMonthEnd)} disabled />

      {/* Projected month-end total = MTD actual production + remaining forecast */}
      <FormField label="Month-End" value={formatMoney((mtd || 0) + forecastToMonthEnd)} disabled />

      {/* Action buttons: Edit (view mode) | Cancel + Submit (create/edit mode) */}
      <ForecastFormActions
        canEdit={canEdit}
        hasExisting={hasExisting}
        isEditing={isEditing}
        canSubmit={canSubmit}
        isPending={isPending}
        isTransitioning={isTransitioning}
        onEdit={() => setIsEditing(true)}
        onCancel={handleCancel}
      />

      <MutationError
        show={createForecastMutation.isError || updateForecastMutation.isError}
        error={createForecastMutation.error || updateForecastMutation.error}
        fallbackMessage="Failed to save forecast"
      />
    </FormSubmit>
  );
}
